import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import API, { commonApiInvoker } from '@api/api'
import { Button, Divider, Empty, Image, Input, message, Select, Space, Spin, Checkbox, Radio, Popover } from 'antd'
import { SearchOutlined, CheckOutlined } from '@ant-design/icons'
import InfiniteScroll from 'react-infinite-scroll-component'
import Utils from "@utils/utils"
import eventBus from '@utils/eventBus';
import { useLocation } from 'react-router-dom'
import { flushSync } from 'react-dom'
import PSAIModal from "@PSAIComponents/PSAIModal";
import JSZip from 'jszip'
import axios from 'axios'
import { saveAs } from "file-saver";
import DeliveryImageItem from "../DeliveryImageItem";
import Iconfont from '@utils/Iconfont';

const uncheckIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.38428 9.55206L7.76003 14.9278L17.6156 5.07227" stroke="#BACCF4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
)
const checkIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.38428 9.55206L7.76003 14.9278L17.6156 5.07227" stroke="#006EFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
)

const DeliveryItem = ({
    item,
    onPreview,
    onSubmit,
    isMultiSelect,
    selectRowKeys,
    onSelect,
    onDownload,
    onImageDownload,
    onRefresh,
}) => {
    const {skuCode} = item
    const firstSkuImage = useMemo(() => {
        return item.skuImages?.[0]
    }, [item])

    // 没有全部审核时，点击【提交反馈】时，toast提示
    // 全部已反馈的，不显示按钮
    const allImageSubmited = useMemo(() => {
        const deliveryImages = item.deliveryImages || []
        return deliveryImages.every(item => !!item.deliverySubmit)
    }, [item])
    // 全部已标记的，高亮按钮
    const allImageMarked = useMemo(() => {
        const deliveryImages = item.deliveryImages || []
        return deliveryImages.every(item => {
            return item.status == 2 || // 已通过/已下载
                item.status == 3 || // 不通过
                item.status == -1 // 已取消
        })
    }, [item])
    const handleSubmit = useCallback(() => {
        onSubmit([item.id])
    }, [item, onSubmit])
    const handleDownload = useCallback(() => {
        onDownload([item.id])
    }, [item, onDownload])
    // 多选时，是否选中
    const isSelected = useMemo(() => {
        return selectRowKeys.includes(item.id)
    }, [item, selectRowKeys])
    // 允许多选
    const selectDisabled = useMemo(() => {
        return false
    }, [allImageSubmited, allImageMarked])
    const handleSelect = useCallback((e) => {
        if (!isMultiSelect) return
        e.stopPropagation()
        if (selectDisabled) return
        onSelect(item)
    }, [item, isMultiSelect, onSelect, selectDisabled])

    // 图片多选
    const [isImageMultiSelect, setIsImageMultiSelect] = useState(false)
    const [selectImageKeys, setSelectImageKeys] = useState([])
    // 多选sku时，取消图片多选
    useEffect(() => {
        if (isMultiSelect) setIsImageMultiSelect(false)
    }, [isMultiSelect])
    // 选择图片
    const handleImageSelect = useCallback((item) => {
        const imageId = item.imageId
        setSelectImageKeys(pre => {
            if (pre.includes(imageId)) {
                return pre.filter(item => item != imageId)
            } else {
                return [...pre, imageId]
            }
        })
    }, [])
    // 批量通过
    const handlePass = useCallback(() => {
        PSAIModal.confirm({
            title: `确认通过选中的图片吗？`,
            okText: '确定',
            cancelText: '取消',
            onOk () {
                const promiseList = selectImageKeys.map(imageId => {
                    return API.merchantJudge({
                        annotations: "",
                        deliveryId: item.id,
                        imageId: imageId,
                        pass: true
                    }).then(res => {
                        if (res?.code != 0) throw new Error(res?.message)
                    })
                })
                return Promise.all(promiseList).then(() => {
                    message.success('提交成功')
                    setSelectImageKeys([])
                    onRefresh()
                }).catch(err => {
                    console.error(err)
                    message.warning(err?.message)
                })
            }
        })
    }, [selectImageKeys, item, onRefresh])
    // 批量不通过
    const [rejectOpen, setRejectOpen] = useState(false)
    const [rejectLoading, setRejectLoading] = useState(false)
    const [rejectReason, setRejectReason] = useState()
    const handleReject = useCallback(() => {
        setRejectReason("")
        setRejectOpen(true)
    }, [selectImageKeys, onRefresh])
    const handleRejectSubmit = useCallback(() => {
        if (!rejectReason || !rejectReason.trim()) return message.warning('请填写修改意见')
        setRejectLoading(true)
        const deliveryImages = item.deliveryImages
        const promiseList = selectImageKeys.map(imageId => {
            const imageItem = deliveryImages?.find(item => item.imageId == imageId)
            const historyAnnotations = imageItem?.userPrevImage?.annotations || []
            return API.merchantJudge({
                annotations: [
                    ...getNoCommentAnnotations(historyAnnotations),
                    {comment: rejectReason, timestamp: Date.now()}
                ],
                deliveryId: item.id,
                imageId: imageId,
                pass: false
            }).then(res => {
                if (res?.code != 0) throw new Error(res?.message)
            })
        })
        return Promise.all(promiseList).then(() => {
            message.success('提交成功')
            setSelectImageKeys([])
            onRefresh()
        }).catch(err => {
            console.error(err)
            message.warning(err?.message)
        }).finally(() => {
            setRejectLoading(false)
            setRejectOpen(false)
        })
    }, [rejectReason, selectImageKeys, item, onRefresh])
    // 图片通过
    const handleApprove = useCallback((imageId, deliveryId, needConfirm) => {
        const onOk = () => (
            API.merchantJudge({
                annotations: null,
                deliveryId: deliveryId,
                imageId: imageId,
                pass: true
            }).then(res => {
                if (res?.code != 0) throw new Error(res?.message)
                onRefresh()
            }).catch(err => {
                console.error(err)
                message.warning(err?.message)
            })
        )
        if (!needConfirm) {
            return onOk()
        }
        PSAIModal.confirm({
            title: '是否确定将该图像标记为通过',
            okText: '通过',
            cancelText: '取消',
            onOk () {
                return onOk()
            },
        })
    }, [item, onRefresh])
    // 全选图片
    const handleSelectAll = useCallback(() => {
        setSelectImageKeys(item.deliveryImages?.filter(item => {
            const isDisabled = (item?.deliverySubmit && (item?.status == 2 || item?.status == -1))
            return !isDisabled
        }).map(item => item.imageId) || [])
    }, [item])

    return (
        <>
        <div
            className={`${styles.DeliveryItem} ${isMultiSelect && isSelected ? styles.Selected : ''}`}
            style={isMultiSelect ? (selectDisabled ? {cursor:"not-allowed"} : {cursor:"pointer"}) : {}}
            onClickCapture={handleSelect}
        >
            <div className={styles.DeliveryItemTitle} style={isMultiSelect? {paddingLeft:12} : {}}>
            {
                !isMultiSelect ? null :
                isSelected ? <div className={styles.MultiSelect}><Checkbox style={{lineHeight:1}} checked  /></div> :
                <div className={styles.MultiSelect}><Checkbox style={{lineHeight:1}} /></div>
            }{
                <div className={styles.SkuCode}>{skuCode}</div>
            }{
                isImageMultiSelect ? 
                <Space className={styles.BtnGroup} size={10}>
                    <span style={{fontSize:14, lineHeight:20, color: "rgba(0, 0, 0, 0.5)"}}>已选图片：{selectImageKeys.length}组</span>
                    <Space size={8}>
                        <button key="download" className={styles.btn} onClick={handlePass}>通过</button>
                        <button key="feedback" className={styles.btn} onClick={handleReject}>不通过</button>
                        <button key="all" className={styles.btn} onClick={handleSelectAll}>全选</button>
                        <button key="cancel" className={styles.btn} onClick={() => setIsImageMultiSelect(false)}>取消</button>
                    </Space>
                </Space> :
                <Space className={styles.BtnGroup}>
                {
                    isMultiSelect ? <button style={{width: 96}} className={styles.btn} disabled>批量操作</button> :
                    <button style={{width: 96}} className={styles.btn} onClick={() => {setIsImageMultiSelect(true);setSelectImageKeys([])}}>批量操作</button>
                } {
                    isMultiSelect ? <button style={{width: 96}} className={styles.btn} disabled>下载</button> :
                    <button style={{width: 96}} className={styles.btn} onClick={handleDownload}>下载</button>
                } {
                    isMultiSelect ? <button style={{width: 144}} className={styles.btn} disabled><Iconfont type="icon-delivery-submit" style={{fontSize:16}} />提交反馈</button> :
                    allImageSubmited ? <button style={{width: 144}} className={styles.btn} disabled><CheckOutlined style={{fontSize:16}} />已提交</button> :
                    allImageMarked ? <button style={{width: 144}} className={`${styles.btn} ${styles.Primary}`} onClick={handleSubmit}><Iconfont type="icon-delivery-submit" style={{fontSize:16}} />提交反馈</button> :
                    <Popover
                        content={<span>请先标记SKU内<strong>待验收</strong>图片是否通过</span>}
                        placement='bottom'
                        arrow={false}
                    >
                        <button style={{width: 144}} className={styles.btn} disabled><Iconfont type="icon-delivery-submit" style={{fontSize:16}} />提交反馈</button>
                    </Popover>
                }
                </Space>
            }
            </div>
            <div className={styles.DeliveryItemContent}>
                <div style={{width: 60, height: 80}}>
                    <Image
                        rootClassName={styles.ImageItem}
                        src={firstSkuImage ? Utils.getImgThumbnail(firstSkuImage) : require('@assets/images/delivery/cloth_default.png')}
                        alt="sku"
                        fallback={require('@assets/images/fallback.png')}
                        preview={firstSkuImage ? {src: firstSkuImage} : false}
                    />
                </div>
                <div className={styles.DeliveryItemImages}>
                {
                    item.deliveryImages.map(item2 => (
                        <div key={item2.imageId} style={{width: 120, height: 160}}>
                            <DeliveryImageItem
                                key={item2.imageId}
                                item={{
                                    ...item2,
                                    deliveryId: item.id,
                                }}
                                onPreview={(data) => onPreview({...data, spuId: item2.spuCode})}
                                onPass={(imageId, needConfirm) => handleApprove(imageId, item.id, needConfirm)}
                                onDownload={onImageDownload}
                                isImageMultiSelect={isImageMultiSelect}
                                selectImageKeys={selectImageKeys}
                                onSelect={handleImageSelect}
                            />
                        </div>
                    ))
                }
                </div>
            </div>
        </div>
        <PSAIModal
            title="修改意见"
            width={556}
            open={rejectOpen}
            confirmLoading={rejectLoading}
            destroyOnClose
            onOk={handleRejectSubmit}
            onCancel={() => setRejectOpen(false)}
        >
            <div className={styles.RejectForm}>
                <div>选择【不通过】时，必须填写修改意见</div>
                <Input.TextArea
                    placeholder="请详细描述您的修改意见..."
                    autoSize={{ minRows: 5, maxRows: 5 }}
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                />
            </div>
        </PSAIModal>
        </>
    )
}

export default DeliveryItem

// 修改意见, 兼容结构为 { label: string, timestamp: datetime } 或 { text: string, timestamp: datetime } 或 { comment: string, timestamp: datetime }
var isCommentAnnotation = (item) => {
    if (!item || typeof item !== 'object') return false
    return (!!item.label || !!item.text || !!item.comment) && !item.snapshot
}

var getNoCommentAnnotations = (annotations = []) => {
    return annotations.filter(item => !isCommentAnnotation(item))
}
